import { useMutation, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IShippingCreateRequest } from "../models/Shipping";


export default function useShippingBulk() {
  const { get, post, postRaw, del } = useApiHelper();
  const queryClient = useQueryClient();
  const bulkShipping = useMutation(
    (partIds: string[]) => post(`/shipping/bulkship`, partIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["pagedTable"]);
      }
    }
  );

  // Work order parts are often never listed or sold online, so the order/offer
  // checks that guard the eBay flow are skipped here.
  const bulkShippingWithoutOrder = useMutation(
    (partIds: string[]) => post(`/shipping/bulkship?requireOrder=false`, partIds),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["pagedTable"]);
      }
    }
  );

  const shippingLabels = useMutation(
    (req: IShippingCreateRequest[]) => postRaw(`/shipping/bulk`, { shipments: req }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["parts"]);
      },
    }
  );

  const bulkPrintShippingLabels = useMutation(
    async (documentIds: string[]) => {
      const response = await postRaw(`/shipping/bulk/print`, { documentIds });

      if (!response.ok) {
        let message = "Failed to prepare print batch.";

        try {
          const payload = await response.json();
          message = payload?.message ?? message;
        } catch {
          try {
            const text = await response.text();
            if (text) {
              message = text;
            }
          } catch {
          }
        }

        throw new Error(message);
      }

      const contentType = response.headers.get("Content-Type") ?? "";
      if (!contentType.toLowerCase().includes("application/pdf")) {
        throw new Error("Print batch did not return a PDF.");
      }

      return response.blob();
    }
  );

  return {
    bulkShipping,
    bulkShippingWithoutOrder,
    shippingLabels,
    bulkPrintShippingLabels,
  };
}
